import { BASE_URL as serverURL } from "../Common/CommonExport";

/**
 * Enhanced Network Speed Checker
 * Based on GeeksforGeeks approach but improved for production use
 * Tests both upload and download speeds with multiple samples
 *
 * Reference: https://www.geeksforgeeks.org/javascript/how-to-detect-network-speed-using-javascript/
 */
class NetworkSpeedChecker {
  constructor() {
    // Minimum speed requirements based on Recorder.jsx and AudioRecorder.jsx configurations
    // Video: 0.9 Mbps video + 0.096 Mbps audio = ~1 Mbps recording, but need 5 Mbps upload for smooth uploads
    // Audio: 0.096 Mbps recording, but need 1 Mbps upload for smooth uploads
    this.requirements = {
      video: {
        upload: 5 * 1024 * 1024, // 5 Mbps in bps
        download: 2 * 1024 * 1024, // 2 Mbps in bps
      },
      audio: {
        upload: 1 * 1024 * 1024, // 1 Mbps in bps
        download: 2 * 1024 * 1024, // 2 Mbps in bps
      },
    };

    // Test configuration
    this.config = {
      testFileSize: 2 * 1024 * 1024, // 2    MB test file (larger = more accurate)
      numSamples: 3, // Take 3 samples and average
      timeout: 30000, // 30 seconds timeout
    };
  }

  /**
   * Method 1: Enhanced Download Speed Test (Improved GeeksforGeeks approach)
   * Uses multiple samples and prevents caching
   */
  async testDownloadSpeed() {
    const speeds = [];
    const errors = [];

    // Test multiple times for accuracy
    for (let i = 0; i < this.config.numSamples; i++) {
      try {
        // Use a test file from your own server
        // Add cache-busting parameter to prevent browser caching
        const testFileUrl = `${serverURL}/speed-test/download?t=${Date.now()}&sample=${i}`;

        const timeStart = performance.now();

        const response = await fetch(testFileUrl, {
          method: "GET",
          cache: "no-cache",
          headers: {
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const blob = await response.blob();
        const timeEnd = performance.now();

        const duration = (timeEnd - timeStart) / 1000; // Convert to seconds
        const fileSizeBits = blob.size * 8;
        const speedBps = fileSizeBits / duration;

        speeds.push(speedBps);

        // Small delay between samples
        if (i < this.config.numSamples - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      } catch (error) {
        console.error(`Download test sample ${i + 1} failed:`, error);
        // Check for internet disconnection errors
        const errorMessage = error.message || error.toString() || "";
        if (
          errorMessage.includes("ERR_INTERNET_DISCONNECTED") ||
          errorMessage.includes("Failed to fetch") ||
          errorMessage.includes("NetworkError") ||
          error.name === "NetworkError" ||
          (error.name === "TypeError" && errorMessage.includes("fetch"))
        ) {
          errors.push("ERR_INTERNET_DISCONNECTED");
        } else {
          errors.push(error.message);
        }
      }
    }

    if (speeds.length === 0) {
      return {
        success: false,
        error: "All download tests failed",
        errors: errors,
      };
    }

    // Calculate average speed (more accurate than single sample)
    const avgSpeed = speeds.reduce((a, b) => a + b, 0) / speeds.length;
    const minSpeed = Math.min(...speeds);
    const maxSpeed = Math.max(...speeds);

    return {
      success: true,
      speed: avgSpeed,
      speedMbps: avgSpeed / (1024 * 1024),
      minSpeed: minSpeed / (1024 * 1024),
      maxSpeed: maxSpeed / (1024 * 1024),
      samples: speeds.length,
      duration: null, // Will be calculated from samples
    };
  }

  /**
   * Method 2: Upload Speed Test (Critical for your use case!)
   * This is what the GeeksforGeeks article MISSES
   */
  async testUploadSpeed() {
    const speeds = [];
    const errors = [];

    // Generate test data
    const testData = this.generateTestData(this.config.testFileSize);

    for (let i = 0; i < this.config.numSamples; i++) {
      try {
        const formData = new FormData();
        formData.append("file", testData, `speedtest-${i}.bin`);

        const timeStart = performance.now();

        const response = await fetch(
          `${serverURL}/speed-test/upload?t=${Date.now()}&sample=${i}`,
          {
            method: "POST",
            body: formData,
            cache: "no-cache",
            headers: {
              "Cache-Control": "no-cache",
            },
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        await response.json(); // Wait for response
        const timeEnd = performance.now();

        const duration = (timeEnd - timeStart) / 1000;
        const fileSizeBits = this.config.testFileSize * 8;
        const speedBps = fileSizeBits / duration;

        speeds.push(speedBps);

        // Small delay between samples
        if (i < this.config.numSamples - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      } catch (error) {
        console.error(`Upload test sample ${i + 1} failed:`, error);
        // Check for internet disconnection errors
        const errorMessage = error.message || error.toString() || "";
        if (
          errorMessage.includes("ERR_INTERNET_DISCONNECTED") ||
          errorMessage.includes("Failed to fetch") ||
          errorMessage.includes("NetworkError") ||
          error.name === "NetworkError" ||
          (error.name === "TypeError" && errorMessage.includes("fetch"))
        ) {
          errors.push("ERR_INTERNET_DISCONNECTED");
        } else {
          errors.push(error.message);
        }
      }
    }

    if (speeds.length === 0) {
      return {
        success: false,
        error: "All upload tests failed",
        errors: errors,
      };
    }

    const avgSpeed = speeds.reduce((a, b) => a + b, 0) / speeds.length;
    const minSpeed = Math.min(...speeds);
    const maxSpeed = Math.max(...speeds);

    return {
      success: true,
      speed: avgSpeed,
      speedMbps: avgSpeed / (1024 * 1024),
      minSpeed: minSpeed / (1024 * 1024),
      maxSpeed: maxSpeed / (1024 * 1024),
      samples: speeds.length,
    };
  }

  /**
   * Generate test data blob
   */
  generateTestData(sizeInBytes) {
    const chunks = [];
    const chunkSize = 64 * 1024; // 64 KB chunks
    const chunksCount = Math.ceil(sizeInBytes / chunkSize);

    for (let i = 0; i < chunksCount; i++) {
      const remaining = sizeInBytes - i * chunkSize;
      const currentChunkSize = Math.min(chunkSize, remaining);
      const chunk = new Uint8Array(currentChunkSize);

      // Fill with random data (prevents compression)
      for (let j = 0; j < currentChunkSize; j++) {
        chunk[j] = Math.floor(Math.random() * 256);
      }
      chunks.push(chunk);
    }

    return new Blob(chunks);
  }

  /**
   * Fallback: Simple download test using external image (GeeksforGeeks approach)
   * Use this if backend endpoints are not available
   */
  async testDownloadSpeedSimple(imageUrl, fileSizeBytes) {
    return new Promise((resolve) => {
      const timeStart = performance.now();
      const img = new Image();

      img.onload = function () {
        const timeEnd = performance.now();
        const duration = (timeEnd - timeStart) / 1000;
        const fileSizeBits = fileSizeBytes * 8;
        const speedBps = fileSizeBits / duration;

        resolve({
          success: true,
          speed: speedBps,
          speedMbps: speedBps / (1024 * 1024),
          duration: duration,
          method: "simple-image",
        });
      };

      img.onerror = function () {
        resolve({
          success: false,
          error: "Failed to load test image",
        });
      };

      // Add cache-busting parameter
      img.src = `${imageUrl}?t=${Date.now()}`;
    });
  }

  /**
   * Comprehensive speed test
   */
  async performSpeedTest(recordingType = "video", options = {}) {
    const {
      testUpload = true,
      testDownload = true,
      useSimpleFallback = false,
    } = options;

    const results = {
      recordingType,
      download: null,
      upload: null,
      validation: null,
      overall: false,
    };

    const startTime = performance.now();

    // Test download speed
    if (testDownload) {
      if (useSimpleFallback) {
        // Fallback to simple image-based test
        // Speed test image URL (required environment variable)
        // Speed test image URL (required environment variable)
        const speedTestImageUrl =
          process.env.REACT_APP_SPEED_TEST_IMAGE_URL ||
          "https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg"; // Fallback to a reliable public image
        results.download = await this.testDownloadSpeedSimple(
          speedTestImageUrl, // Large test image
          2 * 1024 * 1024, // 2 MB estimated size
        );
      } else {
        results.download = await this.testDownloadSpeed();
      }
    }

    // Test upload speed (CRITICAL for your use case)
    if (testUpload) {
      results.upload = await this.testUploadSpeed();
    }

    const totalTime = (performance.now() - startTime) / 1000;

    // Validate results
    if (results.upload?.success && results.download?.success) {
      results.validation = this.validateSpeed(
        results.upload.speed,
        results.download.speed,
        recordingType,
      );
      results.overall = results.validation.meetsRequirement;
    } else {
      results.validation = {
        meetsRequirement: false,
        error: "Speed test incomplete",
        uploadSuccess: results.upload?.success || false,
        downloadSuccess: results.download?.success || false,
      };
    }

    results.totalTime = totalTime;

    return results;
  }

  /**
   * Validate speed against requirements
   */
  validateSpeed(uploadSpeedBps, downloadSpeedBps, recordingType) {
    const requirements =
      this.requirements[recordingType] || this.requirements.video;

    const uploadMbps = uploadSpeedBps / (1024 * 1024);
    const downloadMbps = downloadSpeedBps / (1024 * 1024);

    const uploadMeets = uploadSpeedBps >= requirements.upload;
    const downloadMeets = downloadSpeedBps >= requirements.download;

    const overall = uploadMeets && downloadMeets;

    return {
      meetsRequirement: overall,
      upload: {
        meets: uploadMeets,
        speed: uploadMbps.toFixed(2),
        required: (requirements.upload / (1024 * 1024)).toFixed(2),
        unit: "Mbps",
      },
      download: {
        meets: downloadMeets,
        speed: downloadMbps.toFixed(2),
        required: (requirements.download / (1024 * 1024)).toFixed(2),
        unit: "Mbps",
      },
      message: overall
        ? `Network speed is sufficient (${uploadMbps.toFixed(
            2,
          )} Mbps upload, ${downloadMbps.toFixed(2)} Mbps download)`
        : `Network speed is insufficient. Required: ${(
            requirements.upload /
            (1024 * 1024)
          ).toFixed(2)} Mbps upload, ${(
            requirements.download /
            (1024 * 1024)
          ).toFixed(2)} Mbps download`,
    };
  }

  /**
   * Quick check using Network Information API (if available)
   */
  quickCheck(recordingType = "video") {
    if ("connection" in navigator) {
      const connection =
        navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;

      if (connection) {
        // Estimate upload speed from effectiveType
        const uploadEstimates = {
          "4g": 10 * 1024 * 1024, // ~10 Mbps
          "3g": 1.5 * 1024 * 1024, // ~1.5 Mbps
          "2g": 0.1 * 1024 * 1024, // ~0.1 Mbps
          "slow-2g": 0.05 * 1024 * 1024, // ~0.05 Mbps
        };

        const estimatedUpload =
          uploadEstimates[connection.effectiveType] || 1 * 1024 * 1024;
        const downloadSpeed = connection.downlink
          ? connection.downlink * 1024 * 1024
          : null;

        const validation = this.validateSpeed(
          estimatedUpload,
          downloadSpeed || 0,
          recordingType,
          true,
        );

        return {
          available: true,
          effectiveType: connection.effectiveType,
          download: {
            speed: downloadSpeed,
            speedMbps: downloadSpeed ? downloadSpeed / (1024 * 1024) : null,
          },
          upload: {
            speed: estimatedUpload,
            speedMbps: estimatedUpload / (1024 * 1024),
            estimated: true,
          },
          rtt: connection.rtt,
          saveData: connection.saveData,
          validation,
        };
      }
    }
    return {
      available: false,
      message:
        "Network Information API not available. Full speed test required.",
    };
  }
}

// Export singleton instance
export const networkSpeedChecker = new NetworkSpeedChecker();
export default networkSpeedChecker;
